import { useTranslation } from 'react-i18next';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';

import { PageSeo } from '../components/PageSeo';
import { ProjectCard } from '../components/ProjectCard';
import { SubpageLayout } from '../components/SubpageLayout';
import { useCmsProjectItems } from '../hooks/useCmsProjectItems';

const LIST_MAX_PROJECTS = 10;

const PROJECT_CARD_SKELETON_COUNT = 6;

function ProjectsListPageSkeleton({ statusLabel }) {
    return (
        <div role="status" aria-live="polite" aria-busy="true">
            <span className="visually-hidden">{statusLabel}</span>
            <SkeletonTheme
                baseColor="#e8ecf2"
                highlightColor="#f0f3f8"
            >
                <ul className="list-unstyled row row-cols-1 row-cols-md-2 row-cols-lg-3 g-4 mb-0">
                    {Array.from(
                        { length: PROJECT_CARD_SKELETON_COUNT },
                        (_, i) => (
                            <li
                                key={`project-sk-${i}`}
                                className="col d-flex"
                                aria-hidden
                            >
                                <article className="project-card project-card--skeleton w-100 h-100 rounded-4 overflow-hidden">
                                    <div className="project-card-inner project-card-inner--skeleton position-relative h-100">
                                        <Skeleton
                                            className="project-card-skeleton-cover"
                                            height="100%"
                                            width="100%"
                                            borderRadius={0}
                                        />
                                        <div className="project-card-skeleton-overlay position-absolute bottom-0 start-0 end-0 p-4">
                                            <Skeleton
                                                height={26}
                                                width="78%"
                                                className="mb-3"
                                                borderRadius={8}
                                            />
                                            <Skeleton
                                                height={13}
                                                width="100%"
                                                className="mb-2"
                                                borderRadius={6}
                                            />
                                            <Skeleton
                                                height={13}
                                                width="92%"
                                                className="mb-4"
                                                borderRadius={6}
                                            />
                                            <Skeleton
                                                height={38}
                                                width={150}
                                                borderRadius={999}
                                            />
                                        </div>
                                    </div>
                                </article>
                            </li>
                        ),
                    )}
                </ul>
            </SkeletonTheme>
        </div>
    );
}

/**
 * Tüm projeler — URL: /projects (CMS’den en fazla 10 kayıt)
 */
export function ProjectsListPage() {
    const { t } = useTranslation();
    const items = useCmsProjectItems({ maxItems: LIST_MAX_PROJECTS });

    return (
        <>
            <PageSeo
                title={`${t('projectsList.title')} — ${t('header.rotaryAlt')}`}
                description={t('seo.projectsList')}
            />
            <SubpageLayout
                title={t('projectsList.title')}
                subtitle={t('projectsList.subtitle')}
                contentClassName="projects-list-page"
            >
            {items == null ? (
                <ProjectsListPageSkeleton
                    statusLabel={t('projectsList.loading')}
                />
            ) : items.length === 0 ? (
                <p className="text-secondary text-center mb-0 py-5">
                    {t('projectsList.empty')}
                </p>
            ) : (
                <ul className="list-unstyled row row-cols-1 row-cols-md-2 row-cols-lg-3 g-4 mb-0">
                    {items.map((item) => (
                        <li key={item.id} className="col d-flex">
                            <ProjectCard item={item} />
                        </li>
                    ))}
                </ul>
            )}
        </SubpageLayout>
        </>
    );
}
